import {Alert, Button, Group, Select, Stack, Table, Text} from '@mantine/core'
import {IconPlus, IconSearch} from '@tabler/icons-react'

import type {RentalAssetType} from 'src/assets/rental-assets/schemas'
import type {WorkerType} from 'src/assets/worker-types/schemas'
import {truckAndDriverBillingWarning} from 'src/jobs/billing-checks'
import {EQUIPMENT_BILLING_TYPE_OPTIONS} from 'src/jobs/billing-types'
import {
    billsEachLine,
    lineTakesRate,
    orderRatePhrase,
    RENTAL_BILLING_TYPE_OPTIONS,
} from 'src/jobs/order-billing'
import type {CreateJobFormValues} from 'src/jobs/types'
import type {ProjectBillingMethod} from 'src/projects/schemas'

import {crewNote, type LinePick} from './job-entry-logic'
import {AssetLineRow, WorkerLineRow} from './requirement-line-rows'

type Lines = Pick<CreateJobFormValues, 'workerRequirements' | 'assetRequirements'>

type CrewEquipmentSectionProps = {
    lines: Lines
    billingMethod: ProjectBillingMethod
    workerTypes: WorkerType[]
    assetTypes: RentalAssetType[]
    errors: Record<string, string>
    onAddLine: (pick: LinePick) => void
    onLinesChange: (lines: Lines) => void
}

export function CrewEquipmentSection({
    lines,
    billingMethod,
    workerTypes,
    assetTypes,
    errors,
    onAddLine,
    onLinesChange,
}: CrewEquipmentSectionProps) {
    const {workerRequirements, assetRequirements} = lines
    const driver = workerTypes.find((type) => type.isDriver)
    const technician = workerTypes.find((type) => type.isTechnician)
    const searchData = [
        {
            group: 'Crew',
            items: workerTypes.map((type) => ({value: `worker:${type.id}`, label: type.name})),
        },
        {
            group: 'Equipment',
            items: assetTypes
                .filter((type) => type.archivedAt == null)
                .map((type) => ({value: `equipment:${type.id}`, label: type.name})),
        },
    ]
    const note = crewNote(workerRequirements, assetRequirements, workerTypes)
    const billingWarning = truckAndDriverBillingWarning(
        [
            ...workerRequirements.map((row) => ({requirementType: 'worker' as const, ...row})),
            ...assetRequirements.map((row) => ({requirementType: 'rental_asset' as const, ...row})),
        ],
        {workerTypes, rentalAssetTypes: assetTypes},
    )
    const eachLine = billsEachLine(billingMethod)
    const assetTypeOf = (typeId: string | null) => assetTypes.find((type) => type.id === typeId)
    const rateColumns =
        eachLine ||
        assetRequirements.some((row) => assetTypeOf(row.rentalAssetTypeId)?.isDrivenTruck !== true)

    return (
        <Stack gap="sm">
            <Group align="flex-end" gap="sm" wrap="wrap">
                <Select
                    label="Add crew or equipment"
                    placeholder="Search crew or equipment"
                    leftSection={<IconSearch size={16} />}
                    searchable
                    data={searchData}
                    value={null}
                    nothingFoundMessage="Nothing matches"
                    style={{flex: 1, minWidth: 240}}
                    onChange={(value) => {
                        const [kind, typeId] = value?.split(':') ?? []
                        if ((kind === 'worker' || kind === 'equipment') && typeId) {
                            onAddLine({kind, typeId})
                        }
                    }}
                />
                {driver ? (
                    <Button
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => onAddLine({kind: 'worker', typeId: driver.id})}
                    >
                        Driver
                    </Button>
                ) : null}
                {technician ? (
                    <Button
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => onAddLine({kind: 'worker', typeId: technician.id})}
                    >
                        Technician
                    </Button>
                ) : null}
            </Group>

            {workerRequirements.length + assetRequirements.length > 0 ? (
                <Table.ScrollContainer minWidth={640}>
                    <Table withTableBorder verticalSpacing="xs">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Type</Table.Th>
                                <Table.Th w={90}>Quantity</Table.Th>
                                {rateColumns ? (
                                    <>
                                        <Table.Th w={130}>Rate</Table.Th>
                                        <Table.Th w={140}>Billing type</Table.Th>
                                    </>
                                ) : null}
                                <Table.Th w={120}>Est. hours each</Table.Th>
                                <Table.Th w={40} />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {workerRequirements.map((row, index) => (
                                <WorkerLineRow
                                    key={`worker-${index}`}
                                    row={row}
                                    index={index}
                                    name={
                                        workerTypes.find((type) => type.id === row.workerTypeId)
                                            ?.name ?? 'Unknown type'
                                    }
                                    errors={errors}
                                    takesRate={eachLine}
                                    rateColumns={rateColumns}
                                    onChange={(patch) =>
                                        onLinesChange({
                                            workerRequirements: workerRequirements.map(
                                                (line, lineIndex) =>
                                                    lineIndex === index
                                                        ? {...line, ...patch}
                                                        : line,
                                            ),
                                            assetRequirements,
                                        })
                                    }
                                    onRemove={() =>
                                        onLinesChange({
                                            workerRequirements: workerRequirements.filter(
                                                (_, lineIndex) => lineIndex !== index,
                                            ),
                                            assetRequirements,
                                        })
                                    }
                                />
                            ))}
                            {assetRequirements.map((row, index) => {
                                const type = assetTypeOf(row.rentalAssetTypeId)
                                return (
                                    <AssetLineRow
                                        key={`asset-${index}`}
                                        row={row}
                                        index={index}
                                        name={type?.name ?? 'Unknown type'}
                                        errors={errors}
                                        takesRate={lineTakesRate(billingMethod, {
                                            isRentalEquipment: !type?.isDrivenTruck,
                                        })}
                                        rateColumns={rateColumns}
                                        isDrivenTruck={type?.isDrivenTruck === true}
                                        billingTypeOptions={
                                            eachLine
                                                ? EQUIPMENT_BILLING_TYPE_OPTIONS
                                                : RENTAL_BILLING_TYPE_OPTIONS
                                        }
                                        onChange={(patch) =>
                                            onLinesChange({
                                                workerRequirements,
                                                assetRequirements: assetRequirements.map(
                                                    (line, lineIndex) =>
                                                        lineIndex === index
                                                            ? {...line, ...patch}
                                                            : line,
                                                ),
                                            })
                                        }
                                        onRemove={() =>
                                            onLinesChange({
                                                workerRequirements,
                                                assetRequirements: assetRequirements.filter(
                                                    (_, lineIndex) => lineIndex !== index,
                                                ),
                                            })
                                        }
                                    />
                                )
                            })}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            ) : (
                <Text size="sm" c="dimmed">
                    No crew or equipment added
                </Text>
            )}

            {note === 'technicians-only' ? (
                <Text size="sm" c="dimmed" data-testid="technicians-note">
                    No driver needed for technicians
                </Text>
            ) : note === 'no-crew' ? (
                <Text size="sm" c="dimmed" data-testid="no-crew-note">
                    No driver or technician added
                </Text>
            ) : null}
            {eachLine ? null : (
                <Text size="sm" c="dimmed" data-testid="order-rate-lines-note">
                    Billed {orderRatePhrase(billingMethod)}. Crew and truck lines take counts only.
                </Text>
            )}
            {billingWarning && eachLine ? (
                <Alert
                    color="yellow"
                    title="Billing warning"
                    data-testid="truck-driver-billing-warning"
                >
                    {billingWarning}
                </Alert>
            ) : null}
        </Stack>
    )
}
